import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * Live aggregate sponsor counts for the public homepage.
 *
 * Behaviour is deliberately identical to the original inline effect in Home.jsx:
 *  - "loading" -> skeletons, "ready" -> live numbers, "error" -> neutral fallback
 *  - one automatic retry on a first failure, then a graceful fallback
 *  - a background refresh every 60s and on window focus, which never flips a
 *    healthy screen back to the loading/error state
 *  - a failure here never blocks the rest of the page
 *
 * Hardening added during the code-quality pass:
 *  - the effect owns a single stable interval (no re-subscription churn)
 *  - background refreshes are throttled and skipped while the tab is hidden, so
 *    focusing the window repeatedly can no longer spam /api/sponsors/stats
 *  - retry timeouts are cleared on unmount (no setState after teardown)
 */
const POLL_MS = 60000;
const MIN_REFRESH_GAP_MS = 30000;

export default function useSponsorStats() {
  const [stats, setStats] = useState({ total: null, programs: {} });
  const [statsState, setStatsState] = useState("loading");

  const activeRef = useRef(true);
  const lastFetchRef = useRef(0);
  const retryTimerRef = useRef(null);

  const fetchStats = useCallback(async ({ retry = true, background = false } = {}) => {
    if (!activeRef.current) return;
    if (background) {
      const since = Date.now() - lastFetchRef.current;
      if (since < MIN_REFRESH_GAP_MS) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    }
    lastFetchRef.current = Date.now();
    if (!background) setStatsState((s) => (s === "ready" ? s : "loading"));
    try {
      const { data } = await api.get("/sponsors/stats", { timeout: 15000 });
      if (!activeRef.current) return;
      setStats({
        total: typeof data?.total === "number" ? data.total : null,
        programs: data?.programs || {},
      });
      setStatsState(typeof data?.total === "number" ? "ready" : "error");
    } catch (err) {
      if (!activeRef.current) return;
      devWarn("useSponsorStats", err);
      if (retry) {
        retryTimerRef.current = setTimeout(() => {
          if (activeRef.current) fetchStats({ retry: false, background });
        }, 2500);
        return;
      }
      if (!background) setStatsState("error");
    }
  }, []);

  useEffect(() => {
    activeRef.current = true;
    fetchStats();

    const timer = setInterval(() => fetchStats({ retry: false, background: true }), POLL_MS);
    const onFocus = () => fetchStats({ retry: false, background: true });
    window.addEventListener("focus", onFocus);

    return () => {
      activeRef.current = false;
      clearInterval(timer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchStats]);

  /** Manual retry for the error state; always shows feedback. */
  const reload = useCallback(() => fetchStats({ retry: false }), [fetchStats]);

  return { stats, statsState, reload };
}
